import { v4 as uuidv4 } from "uuid"
import type {
  CatalogInclude,
  CatalogRevision,
  CatalogTemplate,
  CatalogWrite,
  PointDefinition,
  TemplateDefinition,
} from "../types/regulatory"
import type { ICloudUser } from "../types"
import { ECloudRole } from "../types"
export const scopeLabels = {
  site: "Site",
  common: "Tronc commun équipement",
  equipment: "Module équipement",
} as const
export function catalogPermissions(user: ICloudUser | null, template?: CatalogTemplate) {
  const roles = user?.Roles?.map((role) => role.Name) ?? []
  const superadmin = roles.includes(ECloudRole.SuperAdmin)
  const manage = superadmin || roles.includes(ECloudRole.Admin)
  const edit =
    manage &&
    !!template &&
    (superadmin || (!!template.enterpriseUuid && template.enterpriseUuid === user?.Enterprise.UUID))
  return { superadmin, manage, edit }
}
export function makeCatalogWrite(
  definition: TemplateDefinition,
  includes: CatalogInclude[],
  base?: CatalogRevision,
  review = false,
): CatalogWrite {
  return {
    definition: { ...structuredClone(definition), version: base ? base.sourceVersion + 1 : 1 },
    includes: includes.map((link) => ({
      templateUuid: link.templateUuid,
      revisionUuid: link.revisionUuid,
    })),
    ...(base ? { expectedCurrentRevisionUuid: base.uuid } : {}),
    review,
  }
}
export function validateDefinition(definition: TemplateDefinition, includes: CatalogInclude[]) {
  const errors: string[] = []
  if (!definition.name.trim()) errors.push("Le nom du modèle est requis.")
  if (!definition.points.length && !includes.length)
    errors.push("Ajoutez au moins un point ou un module.")
  const seen = new Set<string>()
  for (const point of definition.points) {
    if (!/^[a-zA-Z0-9_-]{1,100}$/.test(point.id) || seen.has(point.id))
      errors.push(`Identifiant de point invalide ou dupliqué : ${point.id}`)
    seen.add(point.id)
    if (!point.label.trim()) errors.push("Chaque point doit avoir un libellé.")
    if (point.sourcePdfPages?.some((page) => !Number.isSafeInteger(page) || page < 1))
      errors.push(`Pages PDF invalides : ${point.label}`)
    if (
      point.sourcePart !== undefined &&
      (!Number.isSafeInteger(point.sourcePart) || point.sourcePart < 1)
    )
      errors.push(`Partie source invalide : ${point.label}`)
  }
  if (includes.some((link) => !link.revisionUuid))
    errors.push("Chaque module doit être lié à une révision précise.")
  if (new Set(includes.map((link) => link.templateUuid)).size !== includes.length)
    errors.push("Un module ne peut être inclus qu’une fois.")
  return errors
}
export function moveItem<T>(items: T[], index: number, direction: -1 | 1) {
  const target = index + direction
  if (target < 0 || target >= items.length) return items
  const result = [...items]
  ;[result[index], result[target]] = [result[target]!, result[index]!]
  return result
}
export function newPoint(): PointDefinition {
  return {
    id: `custom_${uuidv4()}`,
    section: "",
    label: "",
    reference: "",
    method: "",
    expected: "",
    unit: "",
    photoRequired: false,
    measurementRequired: false,
  }
}
export function emptyDefinition(): TemplateDefinition {
  return {
    id: "server_assigned",
    name: "",
    scope: "site",
    version: 1,
    reference: "",
    description: "",
    points: [],
    includes: [],
    validated: false,
    archived: false,
    reviewer: "",
    reviewDate: "",
  }
}
export function changedDefinitionKeys(
  before: PointDefinition | TemplateDefinition,
  after: PointDefinition | TemplateDefinition,
) {
  return [...new Set([...Object.keys(before), ...Object.keys(after)])].filter(
    (key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]),
  )
}

// Presentation aliases only: API namespaces and imported identities remain unchanged.
export function catalogSourceLabel(namespace?: string | null): string {
  return namespace?.toLowerCase() === "airecontrole" ? "Catalogue réglementaire" : namespace ?? ""
}
export function catalogSourceNamespace(label: string): string {
  return label.trim().toLowerCase() === "catalogue réglementaire" ? "airecontrole" : label
}
